import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import Select from './Select';
import LoadingSkeleton from '../LoadingSkeleton';

/**
 * A simple wrapper component if you just want to load a list of all the (non-deleted)
 * items a user can see in a select dropdown. Also handles hasura enum tables
 */
function SelectSelfLoading({
  fieldHasuraName = 'name', // name of the column in hasura
  fieldTableName, // name of the table in hasura
  additionalFields = [],
  isEnumTable = false,
  context = {
    headers: {
      'x-hasura-role': 'user',
    },
  },
  where = { deleted_at: { _is_null: true } },
  label,
  ...props
}) {
  // What are we showing people?
  const displayName = label || fieldHasuraName;

  // Set up a dynamic query
  // TODO: Reintroduce default where deleted_at
  const tableQuery = gql`
    query SelectSelfLoading_${fieldTableName}($where: ${fieldTableName}_bool_exp) {
        ${fieldTableName}(where: $where) {
            id
            ${fieldHasuraName}
			${additionalFields?.join('\n')}
        }
    }
    `;

  const enumQuery = gql`
    query SelectSelfLoading_Enum_${fieldTableName}($where: ${fieldTableName}_bool_exp) {
        ${fieldTableName}(where: $where) {
            value
            description
        }
    }
    `;

  // get us the initial values to display
  const { data, loading, error } = useQuery(isEnumTable ? enumQuery : tableQuery, {
    context,
    variables: { where },
  });
  if (error) {
    console.error(error);
    throw error;
  }

  // extract value from data if it applies
  const itemList = useMemo(
    () =>
      isEnumTable
        ? data?.[fieldTableName]?.map((x) => ({ ...x, name: x.value }))
        : data?.[fieldTableName]?.map((x) => ({ id: x.id, name: x[fieldHasuraName] })),
    [data],
  );

  if (loading) {
    return <LoadingSkeleton />;
  }

  return <Select label={displayName} loading={loading} itemList={itemList || []} {...props} />;
}

SelectSelfLoading.propTypes = {
  fieldHasuraName: PropTypes.string,
  fieldTableName: PropTypes.string.isRequired,
  additionalFields: PropTypes.arrayOf(PropTypes.string),
  isEnumTable: PropTypes.bool,
  label: PropTypes.string,
  context: PropTypes.object,
  where: PropTypes.object,
};

export default SelectSelfLoading;
